import type { GameConfig } from '../../data/games';
import type { SessionMode } from '../../utils/trainingProtocol';
import type { ChallengeTier, TrialOutcome, TrialRecord } from './types';

export const PRECISION_MECHANICS = ['reaction', 'search', 'tracking', 'sequence', 'grid', 'nback', 'maze', 'match'];
export const TIER_NAMES = ['进阶', '专家', '大师'] as const;
export const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
export const mean = (values: number[]) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
export const isPrecisionGame = (game: GameConfig) => PRECISION_MECHANICS.includes(game.mechanic);
export const tierLabel = (mode: SessionMode, tier: ChallengeTier) => mode === 'standard' ? '标准练习' : `${TIER_NAMES[tier - 1]}挑战`;

export function getPrecisionRules(game: GameConfig, mode: SessionMode, tier: ChallengeTier) {
  const level = mode === 'standard' ? 0 : tier;
  const difficulty = game.difficulty;
  const intensity = clamp(game.params.speed ?? 1, 0.75, 1.6);
  return {
    level,
    rounds: 8 + level * 2,
    mistakeLimit: mode === 'standard' ? 4 : Math.max(2, 4 - level),
    reactionWindow: Math.round(clamp((1150 - difficulty * 90 - level * 115) / Math.sqrt(intensity), 430, 1250)),
    perfectWindow: Math.round(clamp(390 - difficulty * 20 - level * 25, 220, 370)),
    waitMin: 900,
    waitMax: 2300,
    decoyCount: level === 0 ? 1 : level === 1 ? 2 : 3,
    searchWindow: Math.round(clamp(5300 - difficulty * 300 - level * 450, 2800, 5000)),
    gridSize: Math.min(5, Math.max(3, Math.min(game.params.gridSize ?? 3, 4)) + (level >= 2 ? 1 : 0)),
    trackingSeconds: Math.min(40, game.params.duration ?? 24),
    trackingSpeed: (0.55 + difficulty * 0.12 + level * 0.2) * Math.sqrt(intensity),
    trackingRadius: 11 - difficulty * 0.5 - level * 1.2,
    lossLimitMs: mode === 'standard' ? 6000 : 5500 - level * 700,
  };
}

export function classifyReaction(ms: number, perfectMs: number, deadlineMs: number): TrialOutcome {
  if (ms < 100) return 'early';
  if (ms > deadlineMs) return 'timeout';
  return ms <= perfectMs ? 'perfect' : 'good';
}

export function summarizeTrials(trials: TrialRecord[], planned: number) {
  const successful = trials.filter(t => t.outcome === 'perfect' || t.outcome === 'good');
  const times = successful.flatMap(t => t.responseMs === null ? [] : [t.responseMs]);
  let combo = 0;
  let maxCombo = 0;
  for (const trial of trials) {
    combo = trial.outcome === 'perfect' || trial.outcome === 'good' ? combo + 1 : 0;
    maxCombo = Math.max(maxCombo, combo);
  }
  const perfectCount = trials.filter(t => t.outcome === 'perfect').length;
  // Score is bounded, accuracy-dominant, and independent of the challenge multiplier.
  const score = Math.round(clamp((successful.length * 80 + perfectCount * 15 + maxCombo * 5) / Math.max(1, planned), 0, 100));
  return {
    score,
    accuracy: Math.round(successful.length / Math.max(1, trials.length) * 100),
    responseMs: times.length ? Math.round(mean(times)) : null,
    bestResponseMs: times.length ? Math.round(Math.min(...times)) : null,
    maxCombo,
    perfectCount,
    mistakes: trials.length - successful.length,
    total: trials.length,
    combo,
  };
}

export function getMedals(score: number, accuracy: number, mistakes: number) {
  return [score >= 60, accuracy >= 85, mistakes === 0 && score >= 85];
}

export function trackingTarget(seconds: number, speed: number) {
  const angle = seconds * speed;
  return {
    x: 50 + Math.sin(angle) * 27 + Math.sin(angle * 0.43) * 6,
    y: 50 + Math.sin(angle * 0.72) * 25 + Math.sin(angle * 1.37) * 5,
  };
}

export function trackingQuality(distance: number, radius: number, held: boolean) {
  if (!held) return 0;
  return clamp(100 * (1 - distance / (radius * 1.65)), 0, 100);
}

export interface SearchItem { shape: number; tint: number; rotation: number; }
export interface SearchBoard { items: SearchItem[]; targetIndex: number; rule: string; ruleKind: string; }
export function createSearchBoard(grid: number, round: number, level: number, random = Math.random): SearchBoard {
  const size = grid * grid;
  const targetIndex = Math.floor(random() * size);
  const kind = round % (level >= 2 ? 4 : 3);
  const baseShape = Math.floor(random() * 3);
  const shade = Math.max(10, 24 - level * 3);
  const items = Array.from({ length: size }, () => ({ shape: baseShape, tint: 58, rotation: 0 }));
  let rule = '找出形状不同的一项';
  let ruleKind = '形状辨识';
  if (kind === 0) items[targetIndex].shape = (baseShape + 1) % 3;
  if (kind === 1) {
    rule = '找出颜色更亮的一项'; ruleKind = '明暗辨识';
    items[targetIndex].tint += shade;
  }
  if (kind === 2) {
    rule = '找出方向不同的箭头'; ruleKind = '方向辨识';
    items.forEach(item => { item.shape = 3; });
    items[targetIndex].rotation = level >= 2 ? 45 : 90;
  }
  if (kind === 3) {
    rule = '找出更亮的菱形'; ruleKind = '双重条件';
    items.forEach((item, index) => {
      item.shape = index % 2 === 0 ? 1 : 0;
      item.tint = item.shape === 0 ? 58 + shade : 58;
    });
    items[targetIndex] = { shape: 1, tint: 58 + shade, rotation: 0 };
  }
  return { items, targetIndex, rule, ruleKind };
}
